import type { ResumeDocument } from '@docbee/resume';
import { generatorNames } from '@docbee/resume';
import { createFileRoute, Link, useParams, useSearch } from '@tanstack/react-router';
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { useDebounce } from 'react-use';
import { Button } from '@/components/Button';
import { ResumeEditor } from '@/components/Editor';
import {
  DownloadIcon,
  EditorIcon,
  EnhancedEditorIcon,
  HelpIcon,
  LoadIcon,
  OptionsIcon,
  PlainEditorIcon,
  ResetIcon,
  SaveIcon,
} from '@/components/Icons';
import { Logo } from '@/components/Logo';
import { OptionsForm } from '@/components/OptionsForm';
import { PlainEditor } from '@/components/PlainEditor';
import { Reset } from '@/components/Reset';
import type { PreviewRef } from '@/components/Resume/Preview';
import { clearExamplePDFCache, saveSnapshot } from '@/lib/db';
import { useResume } from '@/lib/hooks';
import { setPersistedState } from '@/lib/usePersistedState';

const Preview = lazy(() =>
  import('@/components/Resume/Preview').then((mod) => ({ default: mod.Preview }))
);
const PageSelector = lazy(() =>
  import('@/components/Resume/Preview').then((mod) => ({ default: mod.PageSelector }))
);

const resumeEditorDebounceDelay = 500;
const generatorOptionsDebounceDelay = 450;

export const Route = createFileRoute('/edit/$resumeId')({
  validateSearch: (search) => ({
    editor: search.editor as string | undefined,
  }),
  component: EditPage,
});

function EditPage() {
  const { resumeId } = useParams({ strict: false });
  const { editor } = useSearch({ strict: false });
  const defaultPlainEditor = editor === 'plain';

  if (!resumeId) {
    return null;
  }

  const {
    resume,
    parsedResume,
    generator,
    setResume: rawSetResume,
    setGenerator: rawSetGenerator,
    generatorOptions,
    setGeneratorOptions: rawSetGeneratorOptions,
    generatorOptionsSchema,
    loadFromDataFile: rawLoadFromDataFile,
    resetToExample: rawReset,
    resetGeneratorOptions: rawResetGeneratorOptions,
    saveDataFile,
  } = useResume(resumeId);

  // When editing the example resume, any change marks it as "edited" so the
  // home page switches from the pre-rendered static PDF to dynamic rendering.
  const isExample = resumeId === 'example';
  const setResume = isExample
    ? (value: string) => {
        setPersistedState('docbee:example:edited', true);
        void clearExamplePDFCache();
        rawSetResume(value);
      }
    : rawSetResume;
  const setGenerator = isExample
    ? (value: string) => {
        setPersistedState('docbee:example:edited', true);
        void clearExamplePDFCache();
        rawSetGenerator(value);
      }
    : rawSetGenerator;
  const setGeneratorOptions = isExample
    ? (gen: string, opts: Record<string, unknown>) => {
        setPersistedState('docbee:example:edited', true);
        void clearExamplePDFCache();
        rawSetGeneratorOptions(gen, opts);
      }
    : rawSetGeneratorOptions;
  const loadFromDataFile = isExample
    ? async (file: File) => {
        setPersistedState('docbee:example:edited', true);
        void clearExamplePDFCache();
        await rawLoadFromDataFile(file);
      }
    : rawLoadFromDataFile;
  const reset = isExample
    ? () => {
        setPersistedState('docbee:example:edited', true);
        void clearExamplePDFCache();
        rawReset();
      }
    : rawReset;
  const resetGeneratorOptions = isExample
    ? () => {
        setPersistedState('docbee:example:edited', true);
        void clearExamplePDFCache();
        rawResetGeneratorOptions();
      }
    : rawResetGeneratorOptions;

  const previewRef = useRef<PreviewRef>(null);
  const loadInputRef = useRef<HTMLInputElement>(null);
  const [leftPanelView, setLeftPanelView] = useState<'editor' | 'options'>('editor');
  const [isPlainEditor, setIsPlainEditor] = useState(defaultPlainEditor);
  const [debouncedParsedResume, setDebouncedParsedResume] = useState<ResumeDocument | null>(null);
  const [debouncedGeneratorOptions, setDebouncedGeneratorOptions] = useState<
    Record<string, unknown>
  >({});

  useDebounce(
    () => {
      setDebouncedParsedResume(parsedResume);
    },
    resumeEditorDebounceDelay,
    [parsedResume]
  );

  useDebounce(
    () => {
      setDebouncedGeneratorOptions(generatorOptions[generator] as Record<string, unknown>);
    },
    generatorOptionsDebounceDelay,
    [generatorOptions[generator]]
  );

  useEffect(() => {
    if (!debouncedParsedResume) return;

    const timer = setTimeout(async () => {
      if (!previewRef.current) return;
      const image = await previewRef.current.savePreviewPage();
      if (!image) return;

      await saveSnapshot({
        resumeId,
        image,
        timestamp: Date.now(),
        generator,
      });
    }, 1000);

    return () => clearTimeout(timer);
  }, [debouncedParsedResume, resumeId, generator]);

  const handleLoadFile = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        console.log(`Loading file: ${file.name}`);
        await loadFromDataFile(file);
      }
      event.target.value = '';
    },
    [loadFromDataFile]
  );

  const handleSaveFile = useCallback(async () => {
    await saveDataFile();
  }, [saveDataFile]);

  const handleReset = useCallback(() => {
    reset();
  }, [reset]);

  const handleGeneratorChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setGenerator(e.target.value);
    },
    [setGenerator]
  );

  const handleDownloadPDF = useCallback(() => {
    if (previewRef.current) {
      previewRef.current.download();
    }
  }, []);

  return (
    <div className="flex flex-col h-screen">
      <div className="flex flex-row items-center justify-between gap-4 p-4">
        <Link to="/">
          <Logo className="text-4xl" hoverZoom={true} />
        </Link>

        <Link to="/list" className="text-xs font-mono underline underline-offset-2 uppercase">
          <Button>List</Button>
        </Link>
      </div>

      <input
        ref={loadInputRef}
        type="file"
        accept="application/json"
        className="hidden"
        onChange={handleLoadFile}
      />

      <div className="flex flex-col lg:flex-row">
        <div className="flex flex-col gap-4 w-full h-auto lg:h-screen lg:w-1/2 overflow-auto lg:overflow-y-scroll px-4 border-b lg:border-b-0 border-gray-200 pb-4 scrollbar ">
          <div className="flex flex-row flex-wrap items-stretch gap-2">
            {leftPanelView === 'editor' && (
              <Button onClick={() => setIsPlainEditor(!isPlainEditor)}>
                {isPlainEditor ? <EnhancedEditorIcon /> : <PlainEditorIcon />}
                {isPlainEditor ? 'Enhanced Editor' : 'Plain Editor'}
              </Button>
            )}

            <Button
              onClick={() => setLeftPanelView(leftPanelView === 'editor' ? 'options' : 'editor')}
            >
              {leftPanelView === 'editor' ? <OptionsIcon /> : <EditorIcon />}
              {leftPanelView === 'editor' ? 'Style Options' : 'Back to Resume Editor'}
            </Button>

            <div className="hidden todo">
              <HelpIcon className="text-gray-500" />
            </div>

            {leftPanelView === 'editor' && (
              <>
                <Button onClick={() => loadInputRef.current?.click()}>
                  <LoadIcon />
                  Load
                </Button>

                <Button onClick={handleSaveFile}>
                  <SaveIcon />
                  Save
                </Button>

                <Reset reset={handleReset}>
                  <Button>
                    <ResetIcon data-slot="icon" />
                    Reset
                  </Button>
                </Reset>
              </>
            )}
          </div>

          {leftPanelView === 'editor' ? (
            <div className="h-screen">
              {isPlainEditor ? (
                <PlainEditor
                  value={resume}
                  onValueChange={(value) => {
                    setResume(value || '');
                  }}
                />
              ) : (
                <ResumeEditor
                  value={resume}
                  onValueChange={(value) => {
                    setResume(value || '');
                  }}
                />
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              <h3 className="text-xs font-mono font-bold uppercase tracking-wider">
                Style Options ({generator})
              </h3>

              <OptionsForm
                schema={generatorOptionsSchema}
                value={generatorOptions[generator] as Record<string, unknown>}
                onChange={(value) => {
                  setGeneratorOptions(generator, value);
                }}
              />

              <Button onClick={resetGeneratorOptions}>
                <ResetIcon />
                Reset style options to defaults
              </Button>
            </div>
          )}
        </div>

        <div className="w-full lg:w-1/2 pt-5 lg:pt-0 flex flex-col gap-4 px-4">
          <div className="w-full flex flex-row flex-wrap gap-4 items-center">
            {generator && (
              <div className="flex flex-row items-center gap-2">
                <label
                  htmlFor="format-select"
                  className="text-[10px] md:text-sm text-primary uppercase text-right font-mono"
                >
                  Change style
                </label>
                <select
                  id="format-select"
                  className="text-xs font-mono uppercase px-2 py-1 text-primary bg-neutral-50 hover:bg-gray-100 border-b-3 border-x border-t rounded-xs cursor-pointer duration-200 transition-all outline-primary"
                  value={generator}
                  onChange={handleGeneratorChange}
                >
                  {generatorNames.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <Button onClick={handleDownloadPDF}>
              <DownloadIcon />
              Download PDF
            </Button>
          </div>

          <Suspense fallback={<div>Loading preview...</div>}>
            <Preview
              resume={debouncedParsedResume}
              generator={generator}
              generatorOptions={debouncedGeneratorOptions}
              pageSelector={PageSelector}
              ref={previewRef}
            />
          </Suspense>
        </div>
      </div>
    </div>
  );
}
